import React, { useEffect, useState } from 'react';
import { BuildingIcon, CalendarDaysIcon, CalendarIcon, ClipboardListIcon, DatabaseIcon, FileSpreadsheetIcon, GraduationCapIcon, MailIcon, MegaphoneIcon, MessageCircleIcon, SendIcon, WebhookIcon, WorkflowIcon, ZapIcon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { GOOGLE_CALENDAR, connectGoogleCalendar, getIntegration, listIntegrations } from '../../services/integrationService';
import { PageHeader } from '../../components/crm/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Tag } from '../../components/ui/Tag';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Database: DatabaseIcon,
  Megaphone: MegaphoneIcon,
  MessageCircle: MessageCircleIcon,
  CalendarDays: CalendarDaysIcon,
  GraduationCap: GraduationCapIcon,
  ClipboardList: ClipboardListIcon,
  FileSpreadsheet: FileSpreadsheetIcon,
  Webhook: WebhookIcon,
  Mail: MailIcon,
  Calendar: CalendarIcon,
  Zap: ZapIcon,
  Workflow: WorkflowIcon,
  Send: SendIcon
};

/** Integración con configuración real en el frontend. */
const GOOGLE_CALENDAR_ID = 'google-calendar';
export function Integrations() {
  const {
    organizationId
  } = useWorkspace();
  const integrations = listIntegrations();
  const [selected, setSelected] = useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [connectedUrl, setConnectedUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    getIntegration(organizationId, GOOGLE_CALENDAR).then(data => {
      if (!active) return;
      const url = String(data?.config?.bookingUrl ?? '');
      setConnectedUrl(url);
      setBookingUrl(url);
    });
    return () => {
      active = false;
    };
  }, [organizationId]);
  const current = integrations.find(i => i.id === selected);
  const isCalendar = current?.id === GOOGLE_CALENDAR_ID;
  async function handleConnect() {
    setSaving(true);
    setError(null);
    try {
      await connectGoogleCalendar(organizationId, bookingUrl.trim());
      setConnectedUrl(bookingUrl.trim());
      setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar.');
    } finally {
      setSaving(false);
    }
  }
  return <>
      <PageHeader title="Integraciones" description="Conecta AVA con las herramientas que ya usa tu empresa." data-id="element-1328" data-mp="24mxn3icq2l70" />

      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-1329" data-mp="1quhsn5xvzvhp">
        {integrations.map(integration => {
        const Icon = ICONS[integration.icon] ?? BuildingIcon;
        const soon = integration.status === 'proximamente';
        const calendar = integration.id === GOOGLE_CALENDAR_ID;
        const connected = calendar && Boolean(connectedUrl);
        return <li key={integration.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1330" data-mp="2xp8fv0ugaxyh">
              <div className="flex items-start gap-3" data-id="element-1331" data-mp="37gus90iiz32t">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-1332" data-mp="37ifm0nx0mhzm">
                  <Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1333" data-mp="2erxmfwvi2yqy" />
                </span>
                <div className="min-w-0" data-id="element-1334" data-mp="2aqc2xsognm7s">
                  <h2 className="text-sm font-semibold text-foreground" data-id="element-1335" data-mp="2arnewfs2809z">
                    {integration.name}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground" data-id="element-1336" data-mp="2oo0o71mfwvdd">
                    {calendar ? 'Calendario global de reservas para todas tus landings.' : integration.description}
                  </p>
                  {connected && <p className="mt-2 truncate text-xs text-muted-foreground" data-id="element-1337" data-mp="3v5xk116vicb1">
                      {connectedUrl}
                    </p>}
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4" data-id="element-1338" data-mp="29oevp7y5ws4h">
                <Tag data-id="element-1339" data-mp="2epce9ln33o6i">
                  {soon ? 'Próximamente' : connected ? 'Conectado' : 'No conectado'}
                </Tag>
                <Button size="sm" variant={soon ? 'ghost' : 'secondary'} disabled={soon} onClick={() => setSelected(integration.id)} data-id="element-1340" data-mp="2no7hcouu0i5l">
                  {connected ? 'Configurar' : 'Conectar'}
                </Button>
              </div>
            </li>;
      })}
      </ul>

      <Modal open={Boolean(current)} onClose={() => setSelected(null)} title={current ? `Conectar ${current.name}` : ''} footer={isCalendar ? <div className="flex justify-end gap-2" data-id="element-1342" data-mp="3bbfnta3zxx97">
              <Button variant="ghost" onClick={() => setSelected(null)} data-id="element-1343" data-mp="zqf457uo05y9">
                Cancelar
              </Button>
              <Button onClick={handleConnect} loading={saving} data-id="element-1344" data-mp="uqaotl5hth9m">
                Guardar y aplicar
              </Button>
            </div> : <div className="flex justify-end" data-id="element-1345" data-mp="lqnj4n5qdpar">
              <Button variant="secondary" onClick={() => setSelected(null)} data-id="element-1346" data-mp="lrynop70312x">
                Entendido
              </Button>
            </div>} data-id="element-1341" data-mp="32ewu3vnxy5bk">
        {isCalendar ? <div className="space-y-4" data-id="element-1347" data-mp="1egclb3u6fvdy">
            <p className="text-sm text-muted-foreground" data-id="element-1348" data-mp="1ehfe5hb287wi">
              Pega el enlace público de reservas de tu Google Calendar. Se usará
              automáticamente en todas las landings que utilicen el calendario
              principal, sin configurarlas una a una.
            </p>
            <Input id="google-calendar-url" label="Enlace de reservas" type="url" placeholder="https://calendar.app.google/tu-enlace" value={bookingUrl} onChange={e => setBookingUrl(e.target.value)} data-id="element-1349" data-mp="226qi17npiiev" />
            {error && <p role="alert" className="text-sm text-red-600" data-id="element-1350" data-mp="27i3yvddn3xv3">
                {error}
              </p>}
          </div> : <p className="text-sm text-muted-foreground" data-id="element-1351" data-mp="2bhwbc165mo5q">
            La conexión con {current?.name} se activará cuando se configuren las
            credenciales correspondientes. Ninguna clave se guarda en el navegador.
          </p>}
      </Modal>
    </>;
}